<template>
	<tr>
		<td>
			<PermissionsWrapper
				:showDisabledIfNotVisible="true"
				:permissionIds="[Permissions.DisbursementBatches]"
				:permissionLevel="PermissionLevels.Update"
			>
				<label>
					<input
						type="checkbox"
						v-model="isIn"
						:disabled="isBatchApproved || isProcessing || isToggling"
						@change="toggleAddRemoveAsync"
					/>
					<span class="custom-checkbox"></span>
				</label>
			</PermissionsWrapper>
		</td>
		<td>{{ payoff?.lenderName }}</td>
		<td>{{ payoff?.borrowerName }}</td>
		<td>{{ payoff?.disbursementAmountFormatted }}</td>
		<td>{{ payoff?.disbursementStatusHumanized }}</td>
		<td>{{ payoff?.disbursementMethodHumanized }}</td>
		<td>{{ payoff?.referenceId }}</td>
		<td>{{ payoff?.lenderUseOnly }}</td>
		<td>{{ payoff?.sequenceNumber }}</td>
	</tr>
</template>

<script setup lang="ts">
	import { ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { BatchReferenceModel } from '../../models/payoffBatches';

	const props = defineProps<{
		batchId?: number;
		payoffReference: BatchReferenceModel;
		isBatchApproved?: boolean;
		isProcessing?: boolean;
		onBatchUpdateAsync: () => Promise<void>;
	}>();

	const isIn = ref(!props.payoffReference.isDeleted);
	const isToggling = ref(false);

	const payoff = computed(() => props.payoffReference.payoff);

	const toggleAddRemoveAsync = async () => {
		if (props.isBatchApproved || props.isProcessing || isToggling.value) {
			return;
		}

		isToggling.value = true;

		try {
			await updateReferenceAsync(isIn.value ? 'add' : 'remove');
		} catch (error) {
			console.error(error);
		} finally {
			isToggling.value = false;
		}
	};

	const updateReferenceAsync = async (action: 'add' | 'remove') => {
		try {
			await axios.post(`/api/payoffbatches/${props.batchId}/${action}/${props.payoffReference.payoffId}`);
			await props.onBatchUpdateAsync();
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to ${action} the payoff ${action === 'add' ? 'to' : 'from'} the batch.`
			});

			isIn.value = !isIn.value; // reset
		}
	};
</script>

<!-- todo: create a standard checkbox component -->

<style scoped>
	td {
		padding: 10px 5px;
	}

	input[type='checkbox'] {
		position: absolute;
		opacity: 0;
		cursor: pointer;
	}

	.custom-checkbox {
		display: inline-block;
		width: 25px;
		height: 25px;
		background-color: white;
		border: 2px solid #ccc;
		border-radius: 4px;
		position: relative;
		transition: background-color 0.2s, opacity 0.2s;
	}

	.custom-checkbox:after {
		content: '';
		position: absolute;
		display: none;
		left: 7px;
		top: 3px;
		width: 6px;
		height: 12px;
		border: solid white;
		border-width: 0 2px 2px 0;
		transform: rotate(45deg);
	}

	input[type='checkbox']:checked + .custom-checkbox {
		background-color: black;
		border: 2px solid black;
	}

	input[type='checkbox']:checked + .custom-checkbox:after {
		display: block;
	}

	.custom-checkbox:hover {
		background-color: #ccc;
	}

	input[type='checkbox']:checked + .custom-checkbox:hover {
		background-color: #444;
	}

	input[type='checkbox']:disabled:checked + .custom-checkbox {
		background-color: #ccc;
		border: 2px solid #ccc;
	}

	input[type='checkbox']:disabled + .custom-checkbox {
		background-color: #ccc;
		opacity: 0.7;
		cursor: not-allowed;
	}

	input[type='checkbox']:disabled + .custom-checkbox:hover {
		background-color: #ccc;
	}
</style>
